import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import useApiClient from "../hooks/useApiClient";
import { BasicMutationOptions } from "../queryClient";
import type { SurveyDefinition } from "./Survey";
import { CreateProcedureFormValuesType, EditTenantFormValuesType } from "../components/Form/schemas";

export const useCreateTenantMutation = (
  mutationOptions?: BasicMutationOptions<CreateTenantResponseData, AxiosError>,
) => {
  const { privateApiClient } = useApiClient();

  return useMutation<CreateTenantResponseData, AxiosError, CreateTenantRequestParams>({
    mutationFn: async (data: CreateTenantRequestParams) => (await privateApiClient.post("/tenant", data)).data,
    mutationKey: ["CreateTenant"],
    ...mutationOptions,
  });
};

export const useEditTenantMutation = (mutationOptions?: BasicMutationOptions<EditTenantResponseData, AxiosError>) => {
  const { privateApiClient } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<EditTenantResponseData, AxiosError, EditTenantRequestParams>({
    mutationFn: async ({ tenantId, ...data }) =>
      (await privateApiClient.put(`/tenant/${tenantId}`, data)).data,
    mutationKey: ["EditTenant"],
    ...mutationOptions,
    onSuccess: (data, variables) => {
      queryClient.removeQueries({ queryKey: ["tenant", variables.tenantId], exact: true });
      void queryClient.invalidateQueries({ queryKey: ["tenant"] });
      void queryClient.invalidateQueries({ queryKey: ["publicTenants"] });
      mutationOptions?.onSuccess?.(data);
    },
  });
};

export const useGetTenantsQuery = () => {
  const { privateApiClient } = useApiClient();
  return useQuery<Tenant[], AxiosError>({
    queryFn: async () => (await privateApiClient.get("/tenant")).data,
    queryKey: ["tenant"],
  });
};

export const useGetTenantQuery = (tenantId: string) => {
  const { publicApiClient } = useApiClient();

  return useQuery<Omit<Tenant, "adminEmail">, AxiosError>({
    queryFn: async () => (await publicApiClient.get(`/tenant/${tenantId}`)).data,
    queryKey: ["tenant", tenantId],
  });
};

// ─────── Storage stats (rozmiar zajętego miejsca w MinIO) ───────

export type ProcedureStorageStats = {
  totalBytes: number;
  fileCount: number;
};

export type TenantStorageStats = {
  totalBytes: number;
  fileCount: number;
  byProcedure: Array<{ procedureId: string; bytes: number; files: number }>;
};

export const useGetProcedureStorageStatsQuery = (
  tenantId: string,
  procedureId: string,
  options: { enabled?: boolean } = {},
) => {
  const { privateApiClient } = useApiClient();
  return useQuery<ProcedureStorageStats, AxiosError>({
    queryFn: async () =>
      (await privateApiClient.get(
        `/tenant/${tenantId}/procedure/${encodeURIComponent(procedureId)}/storage-stats`,
      )).data,
    queryKey: ["storage-stats", "procedure", tenantId, procedureId],
    enabled: options.enabled ?? (!!tenantId && !!procedureId),
    staleTime: 30_000, // 30s — listObjects to nie ultra-fast, cache'ujemy
  });
};

export const useGetTenantStorageStatsQuery = (
  tenantId: string,
  options: { enabled?: boolean } = {},
) => {
  const { privateApiClient } = useApiClient();
  return useQuery<TenantStorageStats, AxiosError>({
    queryFn: async () =>
      (await privateApiClient.get(`/tenant/${tenantId}/storage-stats`)).data,
    queryKey: ["storage-stats", "tenant", tenantId],
    enabled: options.enabled ?? !!tenantId,
    staleTime: 30_000,
  });
};

// ─────── Archiwizacja procedury / gminy ───────

/**
 * Eksportuje procedurę jako ZIP (procedure.json + applications + PDF-y +
 * załączniki + opcjonalnie raster). Zwraca Blob — wywołujący sam decyduje,
 * czy pobrać przez `<a download>` czy wstrzyknąć do `URL.createObjectURL`.
 */
export const useExportProcedureMutation = (
  mutationOptions?: BasicMutationOptions<{ blob: Blob; filename: string }, AxiosError>,
) => {
  const { privateApiClient } = useApiClient();
  return useMutation<
    { blob: Blob; filename: string },
    AxiosError,
    { tenantId: string; procedureId: string; includeRaster?: boolean }
  >({
    mutationFn: async ({ tenantId, procedureId, includeRaster }) => {
      const params = includeRaster ? "?includeRaster=true" : "";
      const res = await privateApiClient.get(
        `/tenant/${tenantId}/procedure/${encodeURIComponent(procedureId)}/export${params}`,
        { responseType: "blob" },
      );
      // Filename z Content-Disposition (backend ustawia z timestampem),
      // fallback gdyby header został zjedzony przez proxy.
      const disposition = (res.headers as any)["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/.exec(disposition);
      const filename = match ? match[1] : `procedure_${procedureId}.zip`;
      return { blob: res.data, filename };
    },
    mutationKey: ["procedure", "export"],
    ...mutationOptions,
  });
};

export const useExportTenantMutation = (
  mutationOptions?: BasicMutationOptions<{ blob: Blob; filename: string }, AxiosError>,
) => {
  const { privateApiClient } = useApiClient();
  return useMutation<
    { blob: Blob; filename: string },
    AxiosError,
    { tenantId: string; includeRaster?: boolean }
  >({
    mutationFn: async ({ tenantId, includeRaster }) => {
      const params = includeRaster ? "?includeRaster=true" : "";
      const res = await privateApiClient.get(`/tenant/${tenantId}/export${params}`, {
        responseType: "blob",
      });
      const disposition = (res.headers as any)["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/.exec(disposition);
      const filename = match ? match[1] : `tenant_${tenantId}.zip`;
      return { blob: res.data, filename };
    },
    mutationKey: ["tenant", "export"],
    ...mutationOptions,
  });
};

export type ImportProcedureResult = {
  procedureId: string;
  procedureName: string;
  procedureDisplayName: string;
  counts: {
    applications: number;
    parcelNotes: number;
    drafts: number;
    formFiles: number;
    attachments: number;
  };
  warnings: string[];
};

export const useImportProcedureMutation = (
  mutationOptions?: BasicMutationOptions<ImportProcedureResult, AxiosError>,
) => {
  const { privateApiClient } = useApiClient();
  return useMutation<ImportProcedureResult, AxiosError, { tenantId: string; file: File }>({
    mutationFn: async ({ tenantId, file }) => {
      const formData = new FormData();
      formData.append("file", file, file.name);
      return (
        await privateApiClient.post(`/tenant/${tenantId}/procedure/import`, formData, {
          // ZIP-y procedur z dużą liczbą wniosków + załączników mogą być
          // > 100 MB. Backend ma limit 5 GB — frontend timeoutu nie ustawia
          // (axios default = no timeout), tylko upload progress można dodać
          // później.
        })
      ).data;
    },
    mutationKey: ["procedure", "import"],
    ...mutationOptions,
  });
};

export type ImportTenantResult = {
  tenantId: string;
  displayName: string;
  adminConfirmationKey: string;
  procedures: Array<{ id: string; name: string; displayName: string }>;
  warnings: string[];
};

export const useImportTenantMutation = (
  mutationOptions?: BasicMutationOptions<ImportTenantResult, AxiosError>,
) => {
  const { privateApiClient } = useApiClient();
  return useMutation<
    ImportTenantResult,
    AxiosError,
    { file: File; targetTenantId: string; adminEmail: string }
  >({
    mutationFn: async ({ file, targetTenantId, adminEmail }) => {
      const formData = new FormData();
      formData.append("file", file, file.name);
      formData.append("targetTenantId", targetTenantId);
      formData.append("adminEmail", adminEmail);
      return (await privateApiClient.post(`/admin/tenant/import`, formData)).data;
    },
    mutationKey: ["tenant", "import"],
    ...mutationOptions,
  });
};

/** Wymusza pobranie blobu w przeglądarce (klik `<a download>`). */
export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke później — niektóre przeglądarki potrzebują chwili żeby download wystartował.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ─────── MPZP files (legenda + tekst planu) ───────

export type MpzpFileType = "legenda" | "plan-text";

export const useUploadMpzpFileMutation = (
  mutationOptions?: BasicMutationOptions<MpzpFileMeta, AxiosError>,
) => {
  const { privateApiClient } = useApiClient();
  return useMutation<
    MpzpFileMeta,
    AxiosError,
    { tenantId: string; formId: string; type: MpzpFileType; file: File }
  >({
    mutationFn: async ({ tenantId, formId, type, file }) => {
      const formData = new FormData();
      formData.append("file", file, file.name);
      return (
        await privateApiClient.post(
          `/tenant/${tenantId}/form/${encodeURIComponent(formId)}/mpzp-file/${type}`,
          formData,
        )
      ).data;
    },
    mutationKey: ["mpzp-file"],
    ...mutationOptions,
  });
};

export const useDeleteMpzpFileMutation = (
  mutationOptions?: BasicMutationOptions<unknown, AxiosError>,
) => {
  const { privateApiClient } = useApiClient();
  return useMutation<
    unknown,
    AxiosError,
    { tenantId: string; formId: string; type: MpzpFileType }
  >({
    mutationFn: async ({ tenantId, formId, type }) => {
      return (
        await privateApiClient.delete(
          `/tenant/${tenantId}/form/${encodeURIComponent(formId)}/mpzp-file/${type}`,
        )
      ).data;
    },
    mutationKey: ["mpzp-file"],
    ...mutationOptions,
  });
};

/** Buduje absolutny URL do pliku MPZP (do osadzenia w <a href> / <iframe>). */
/** Komplet plików procedury jednym ZIP-em (publiczne). */
export function getProcedureZipUrl(tenantId: string, formId: string): string {
  const apiUrl = import.meta.env.VITE_API_URL || "";
  return `${apiUrl}/tenant/${tenantId}/form/${encodeURIComponent(formId)}/files.zip`;
}

export function getMpzpFileUrl(tenantId: string, formId: string, type: MpzpFileType): string {
  const apiUrl = import.meta.env.VITE_API_URL || "";
  return `${apiUrl}/tenant/${tenantId}/form/${encodeURIComponent(formId)}/mpzp-file/${type}`;
}

export const useCreateProcedureMutation = (
  mutationOptions?: BasicMutationOptions<CreateProcedureResponseData, AxiosError>,
) => {
  const { privateApiClient } = useApiClient();

  return useMutation<CreateProcedureResponseData, AxiosError, CreateProcedureRequestParams>({
    mutationFn: async ({
      tenant,
      name,
      displayName,
      description,
      activeFrom,
      activeTo,
      files,
      ...payload
    }: CreateProcedureRequestParams) => {
      const formData = new FormData();
      formData.append("name", name);
      formData.append("displayName", displayName);
      formData.append("description", description);
      formData.append("activeFrom", activeFrom.toISOString());
      formData.append("activeTo", activeTo.toISOString());
      formData.append("payload", JSON.stringify(payload));
      files &&
        Array.from(files).forEach((file) => {
          formData.append("files", file, file.name);
        });
      return (await privateApiClient.post(`/tenant/${tenant}/form`, formData)).data;
    },
    mutationKey: ["procedure"],
    ...mutationOptions,
  });
};

export const useDeleteProcedureMutation = (mutationOptions?: BasicMutationOptions<unknown, AxiosError>) => {
  const { privateApiClient } = useApiClient();
  return useMutation<unknown, AxiosError, { tenantId: string; formId: string }>({
    mutationFn: async ({ tenantId, formId }) => {
      return (await privateApiClient.delete(`/tenant/${tenantId}/form/${formId}`)).data;
    },
    mutationKey: ["procedure"],
    ...mutationOptions,
  });
};

/** Aktualizuje metadane procedury (bez plików — zmiany plików przez osobne endpointy). */
export const useUpdateProcedureMutation = (mutationOptions?: BasicMutationOptions<unknown, AxiosError>) => {
  const { privateApiClient } = useApiClient();
  return useMutation<
    unknown,
    AxiosError,
    CreateProcedureFormValuesType & { tenantId: string; formId: string }
  >({
    mutationFn: async ({
      tenantId,
      formId,
      name: _name, // name/slug nie jest zmieniany po utworzeniu
      displayName,
      description,
      activeFrom,
      activeTo,
      files: _files,
      ...payload
    }) => {
      return (
        await privateApiClient.put(`/tenant/${tenantId}/form/${formId}`, {
          name: _name,
          displayName,
          description,
          activeFrom: activeFrom,
          activeTo: activeTo,
          payload,
        })
      ).data;
    },
    mutationKey: ["procedure"],
    ...mutationOptions,
  });
};

/** Dodaje pliki do istniejącej procedury. Zwraca metadane wgranych plików. */
export const useAddProcedureFilesMutation = (
  mutationOptions?: BasicMutationOptions<Array<{ id: string; filename: string; mimetype: string }>, AxiosError>,
) => {
  const { privateApiClient } = useApiClient();
  return useMutation<
    Array<{ id: string; filename: string; mimetype: string }>,
    AxiosError,
    { tenantId: string; formId: string; files: FileList | File[] }
  >({
    mutationFn: async ({ tenantId, formId, files }) => {
      const formData = new FormData();
      Array.from(files).forEach((file) => {
        formData.append("files", file, file.name);
      });
      return (await privateApiClient.post(`/tenant/${tenantId}/form/${formId}/file`, formData)).data;
    },
    mutationKey: ["procedure-files"],
    ...mutationOptions,
  });
};

/** Usuwa pojedynczy plik z procedury. */
export const useDeleteProcedureFileMutation = (mutationOptions?: BasicMutationOptions<unknown, AxiosError>) => {
  const { privateApiClient } = useApiClient();
  return useMutation<unknown, AxiosError, { tenantId: string; formId: string; fileId: string }>({
    mutationFn: async ({ tenantId, formId, fileId }) => {
      return (await privateApiClient.delete(`/tenant/${tenantId}/form/${formId}/file/${fileId}`)).data;
    },
    mutationKey: ["procedure-files"],
    ...mutationOptions,
  });
};

export const useGetProceduresQuery = (tenantId: string) => {
  const { publicApiClient } = useApiClient();

  return useQuery<Procedure[], AxiosError>({
    queryFn: async () => (await publicApiClient.get(`/tenant/${tenantId}/form`)).data,
    // tenantId w kluczu — bez niego cache procedur był współdzielony
    // między gminami (po przełączeniu gminy widać dane poprzedniej).
    queryKey: ["procedures", tenantId],
  });
};

export const useGetProcedureQuery = (tenantId: string, formId: string) => {
  const { publicApiClient } = useApiClient();

  return useQuery<Procedure[], AxiosError>({
    queryFn: async () => (await publicApiClient.get(`/tenant/${tenantId}/form`)).data,
    // Ten sam klucz co lista (celowo) — oba hooki dzielą jeden wpis cache.
    queryKey: ["procedures", tenantId],
    select: (data) => data.filter(({ id }) => formId === id),
  });
};

export type PublicTenantWithProcedures = {
  id: string;
  displayName: string;
  logo?: string;
  primaryColor?: string;
  procedures: Array<{
    id: string;
    name: string;
    displayName: string;
    activeFrom: string;
    activeTo: string;
    isActive: boolean;
  }>;
};

export const usePublicTenantsQuery = () => {
  const { publicApiClient } = useApiClient();
  return useQuery<PublicTenantWithProcedures[], AxiosError>({
    queryFn: async () => (await publicApiClient.get("/tenant/public/list")).data,
    queryKey: ["publicTenants"],
    staleTime: 60_000,
  });
};

export type MpzpSymbolDef = {
  symbol: string;
  nazwa: string;
  paragrafy?: number[];
  strony?: Array<number | null>;
};

export type ProcedurePayload = {
  /** Symbole przeznaczenia terenów TEGO planu (z legendy) — lista wyboru
   *  w pkt 7.3.4 zamiast ogólnej listy MPZP. */
  mpzpSymbols?: MpzpSymbolDef[];
  /** Nazwa warstwy terenów dla mieszkańca, np. "Projekt MPZP Gminy Łącko". */
  mpzpZonesLabel?: string;
  /** Arkusze rysunku planu (zasięg w EPSG:2180 + ID pliku do pobrania). */
  mpzpSheets?: Array<{ label: string; fileId?: string; polygon2180: Array<[number, number]> }>;
  municipalitysGeneralPlanOrLocalDevelopmentPlan: string;
  spatialPlanningActName: string;
  applicationPlace: string;
  typeOfLetter: string;
  spatialPlanningActType: string;
  /** Domyślna widoczność warstw mapy dla tej procedury (ustawiane przez
   *  admina w EditProcedurePage). Partial bo może nie pokrywać wszystkich
   *  kluczy LayerVisibilityState. */
  defaultLayerVisibility?: Record<string, boolean>;
  /** Domyślna kolejność warstw mapy (LayerId dla built-in, `custom:${id}`
   *  dla custom WMS). */
  defaultLayerOrder?: string[];
  /** Domyślne custom WMS/WMTS warstwy dla tej procedury (wybrane przez
   *  admina z katalogu lub dodane jako własny URL). Zapisywane bez `id`
   *  (ID jest generowane ad-hoc na froncie przy hydratacji). */
  defaultCustomWmsLayers?: Array<{
    url: string;
    layerName: string;
    title: string;
    opacity?: number;
    visible?: boolean;
    availableCrs?: string[];
    type?: "wms" | "wmts";
    catalogId?: string;
  }>;
  /** Lista ID wbudowanych warstw (plan, ortho, dzialki, budynki, ...) które
   *  admin ukrył z listy warstw w tej procedurze — user ich nie widzi
   *  w panelu "Warstwy mapy". Można je przywrócić z katalogu "Warstwy
   *  podstawowe" w EditProcedurePage. */
  hiddenBuiltinLayerIds?: string[];
  /** Plik legendy planu (MPZP) — PDF/JPG/PNG/TIF, max 100 MB. */
  mpzpLegenda?: MpzpFileMeta;
  /** Plik tekstu planu (MPZP) — PDF wielostronicowy A4, max 100 MB. */
  mpzpPlanText?: MpzpFileMeta;
  /** Sekcje plików do pobrania na stronie procedury (admin definiuje). */
  downloadSections?: Array<{
    title: string;
    fileIds: string[];
  }>;
  /** Opis konsultacji społecznych (Markdown). */
  consultationDescription?: string;
  /** Geoankieta (art. 8i ust. 1 pkt 4) — definicja pytań, zapisywana
   *  osobnym endpointem PUT /procedure/:id/survey. */
  survey?: SurveyDefinition;
  /** Domyślne centrum mapy dla tej procedury. */
  mapInitialLat?: number;
  mapInitialLng?: number;
  mapInitialZoom?: number;
};

export type MpzpFileMeta = {
  fileId: string;
  s3Key: string;
  filename: string;
  mimetype: string;
  fileSize: number;
  uploadedAt: string;
};

export type Procedure = {
  id: string;
  displayName: string;
  name: string;
  description: string;
  activeFrom: Date;
  activeTo: Date;
  payload: ProcedurePayload;
  files: AttachedFile[];
};
export type AttachedFile = {
  url: string;
  filename: string;
  mimetype: string;
  id: string;
  /** Rozmiar w bajtach (API od migracji tenant_016). */
  fileSize?: number;
};
export type Tenant = {
  displayName: string;
  id: string;
  adminEmail: string;
  payload: Record<string, any>;
};

type CreateTenantResponseData = {
  adminConfirmationKey: string;
  adminId: string;
};
type CreateTenantRequestParams = {
  id: string;
  displayName: string;
  adminEmail: string;
  payload: any;
};

type EditTenantResponseData = {
  id: string;
};
type EditTenantRequestParams = {
  tenantId: string;
  id: string;
  displayName: string;
  payload: Omit<EditTenantFormValuesType, "id" | "displayName">;
};

// TODO: add types
type CreateProcedureResponseData = any;
type CreateProcedureRequestParams = CreateProcedureFormValuesType & {
  tenant: string;
};
